import React, { useState, useEffect } from 'react';
import { Truck, Check, AlertCircle, FileText, Sparkles, HelpCircle } from 'lucide-react';
import { areaClienteService } from '../../services/areaClienteService';

export interface VeiculoPropostaOption {
  key: string;
  pedidoId: number;
  detalheId?: number;
  tipoVeiculoId: number | null;
  nomeVeiculo: string;
  propostaNumero: number;
  valorUnitario?: number;
  quantidade?: number;
  descricaoServico?: string;
  statusProposta?: string;
}

interface SelecaoVeiculoPropostaProps {
  clienteId: number;
  selectedOptionKey: string;
  onSelectOption: (option: VeiculoPropostaOption | null) => void;
}

export function SelecaoVeiculoProposta({
  clienteId,
  selectedOptionKey,
  onSelectOption
}: SelecaoVeiculoPropostaProps) {
  const [loading, setLoading] = useState(true);
  const [opcoes, setOpcoes] = useState<VeiculoPropostaOption[]>([]);
  const [propostasCount, setPropostasCount] = useState(0);

  useEffect(() => {
    loadVeiculosProposta();
  }, [clienteId]);

  const loadVeiculosProposta = async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const propostas = await areaClienteService.getPropostasCliente(clienteId);
      setPropostasCount(propostas.length);

      const parsedOptions: VeiculoPropostaOption[] = [];

      propostas.forEach((prop: any) => {
        const itens = prop.detalhes_do_pedido || [];
        const isApproved = (prop.status || '').toLowerCase().includes('aprovad');

        itens.forEach((item: any, idx: number) => {
          // Identify vehicle types or services mentioned in proposal
          const nomeServico = item.servicos || item.tipo_servico?.tipo || `Item de Transporte #${idx + 1}`;
          
          // Map standard vehicle types if name matches
          let tipoVeiculoId: number | null = null;
          const lower = nomeServico.toLowerCase();
          if (lower.includes('vuc') || lower.includes('3/4') || lower.includes('tres quartos')) tipoVeiculoId = 1;
          else if (lower.includes('toco')) tipoVeiculoId = 2;
          else if (lower.includes('truck')) tipoVeiculoId = 3;
          else if (lower.includes('carreta')) tipoVeiculoId = 4;
          else if (lower.includes('fiorino') || lower.includes('utilit') || lower.includes('van')) tipoVeiculoId = 5;
          else if (item.tipo_servico_id) tipoVeiculoId = Number(item.tipo_servico_id);

          parsedOptions.push({
            key: `prop_${prop.id}_item_${item.id || idx}`,
            pedidoId: prop.id,
            detalheId: item.id ? Number(item.id) : undefined,
            tipoVeiculoId,
            nomeVeiculo: nomeServico,
            propostaNumero: prop.id,
            valorUnitario: item.valor_unitario || item.valor_total,
            quantidade: item.quantidade,
            descricaoServico: item.servicos,
            statusProposta: prop.status || 'Ativa'
          });
        });
      });

      setOpcoes(parsedOptions);

      // Auto-select first option if not selected yet
      if (parsedOptions.length > 0 && !selectedOptionKey) {
        onSelectOption(parsedOptions[0]);
      }
    } catch (err) {
      console.error('Erro ao carregar veículos da proposta do cliente:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val?: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(val || 0);
  };

  if (loading) {
    return (
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-center py-8">
        <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
        <span className="text-xs text-slate-500 font-medium">Buscando veículos e escopos da sua proposta comercial...</span>
      </div>
    );
  }

  if (opcoes.length === 0) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
        <div className="flex items-center space-x-2 border-b border-slate-100 pb-3">
          <Truck className="w-4 h-4 text-red-600" />
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Veículos da Sua Proposta</h2>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 space-y-1">
            <p className="font-bold">Nenhum veículo/item listado nas propostas cadastradas.</p>
            <p className="text-amber-800">
              Não encontramos serviços de veículos parametrizados nas suas propostas comerciais. O tipo de veículo será definido pelo nosso time operacional conforme a carga informada.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <div className="flex items-center space-x-2">
          <Truck className="w-4 h-4 text-red-600" />
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
            3. Selecione o Veículo da Sua Proposta
          </h2>
        </div>
        <span className="text-xs font-semibold px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-emerald-600" />
          {opcoes.length} opção(ões) contratada(s)
        </span>
      </div>

      <p className="text-xs text-slate-500">
        Selecione abaixo o modelo ou categoria de veículo contratado na sua proposta comercial para este transporte:
      </p>

      {/* Grid of proposal vehicles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {opcoes.map((op) => {
          const isSelected = selectedOptionKey === op.key;

          return (
            <div
              key={op.key}
              onClick={() => onSelectOption(op)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between gap-3 ${
                isSelected
                  ? 'border-red-600 bg-red-50/40 shadow-xs'
                  : 'border-slate-200 bg-slate-50/50 hover:border-slate-300 hover:bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center space-x-2.5">
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                    <Truck className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 leading-tight">
                      {op.nomeVeiculo}
                    </h4>
                    <span className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                      <FileText className="w-3 h-3 text-slate-400" />
                      Proposta Comercial #{op.propostaNumero}
                    </span>
                  </div>
                </div>

                <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                  isSelected ? 'bg-red-600 border-red-600 text-white' : 'border-slate-300 bg-white'
                }`}>
                  {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                </div>
              </div>

              {op.valorUnitario ? (
                <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-xs">
                  <span className="text-slate-500 text-[11px]">Tarifa Contratada:</span>
                  <span className="font-bold text-slate-900">{formatCurrency(op.valorUnitario)}</span>
                </div>
              ) : (
                <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-xs">
                  <span className="text-slate-500 text-[11px]">Status da Proposta:</span>
                  <span className="font-semibold text-emerald-700 text-[11px]">{op.statusProposta}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
